// frontend/src/components/booking-management/BookingCreateForm.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BookingConfirmDialog from '../dialogs/BookingConfirmDialog';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import BoardingInfoModal from '../common/BoardingInfoModal';
import { formatDateISO, formatDateLocal, parseLocalDate } from '../../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedCard } from '../themed';
import { usePreferences } from '../../contexts/PreferenceContext';
import { haveSameLocationAddress } from '../../utils/bookingLocationUtils';
import useBookingManagement from '../../hooks/bookingManagement/useBookingManagement';
import useScheduleAvailability from '../../hooks/bookingManagement/useScheduleAvailability';
import { normalizeBookingTime, shortenAddress } from '../../utils/bookingTimeUtils';
import {
  deriveBookingOccurrenceScope,
  enhanceSlotsWithCapacity,
  filterSlotsByAdvanceBooking,
  findApplicableSchedule,
  isDateExcluded,
  isDateInRange,
  isDayIncluded,
  normalizeBookingOccurrenceDate,
  resolveExactDelayIdentity,
} from '../../utils/bookingScheduleUtils';

// Import extracted hooks
import useBookingFormState from '../../hooks/bookingManagement/useBookingFormState';
import useAdminUserSelection from '../../hooks/bookingManagement/useAdminUserSelection';
import useTranslatedPreferences from '../../hooks/bookingManagement/useTranslatedPreferences';
import useBookingValidation from '../../hooks/bookingManagement/useBookingValidation';
import useBookingSubmission from '../../hooks/bookingManagement/useBookingSubmission';
import useRouteManagement from '../../hooks/bookingManagement/useRouteManagement';

// Import extracted form section components
import AdminUserSelectionSection from './form-sections/AdminUserSelectionSection';
import RouteSelectionSection from './form-sections/RouteSelectionSection';
import LocationSelectionSection from './form-sections/LocationSelectionSection';
import DateSelectionSection from './form-sections/DateSelectionSection';
import TimeSelectionSection from './form-sections/TimeSelectionSection';
import PassengerDetailsSection from './form-sections/PassengerDetailsSection';
import ContactVerificationSection from './form-sections/ContactVerificationSection';
import { AlertTriangle } from 'lucide-react';

const normalizeTimeValue = (value) => {
  if (typeof value !== 'string') return '';
  return value.substring(0, 5);
};

const asEnabledFlag = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

// Shared booking creation form (used by riders and admins)
const BookingCreateForm = ({
  currentEntity,
  isAdminBooking = false,
  embedded = false,
  onBookingCreated = null
}) => {
  const { t, i18n } = useTranslation();
  const { background, text, cn } = useTheme();
  const { entitySettings } = usePreferences();
  // Force re-render when preferences change
  useDateTimePreferences();

  const entityId = currentEntity?.entityId;
  const currentUserId = useMemo(() => {
    const storedId = localStorage.getItem('userId');
    return storedId ? parseInt(storedId, 10) : null;
  }, []);

  // ===================================================================
  // EXTRACTED HOOKS - Form State Management
  // ===================================================================

  // Form state hook - manages all booking form fields and UI state
  const {
    booking,
    setBooking,
    status,
    setStatus,
    isWaitlisted,
    setIsWaitlisted,
    submitting,
    verificationError,
    setVerificationError,
  } = useBookingFormState();

  // Additional state not managed by hooks (specific to this component)
  const [, setNormalizedBookingTime] = useState(null);
  const [contactPrefilled, setContactPrefilled] = useState(false);

  // Modal and UI state (must be declared before submission hook)
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [currentBookingDetails, setCurrentBookingDetails] = useState(null);
  const [showPastDepartureDialog, setShowPastDepartureDialog] = useState(false);
  const [pastDepartureMessage, setPastDepartureMessage] = useState('');
  // Waitlist warning state - shown when booking would exceed capacity
  const [showWaitlistWarning, setShowWaitlistWarning] = useState(false);
  const [pendingWaitlistSubmit, setPendingWaitlistSubmit] = useState(null);

  // Processing details for debugging
  const addProcessingDetail = () => {
    // Debug helper - currently unused but kept for future debugging needs
  };

  // Contact prefill for non-admin users
  const fetchCurrentUserContact = useCallback(async () => {
    if (!entityId || isAdminBooking) {
      return;
    }

    try {
      const response = await axios.get(`/api/settings/${entityId}/profile`);
      const phone = response?.data?.data?.phone;
      if (phone) {
        setBooking((prev) => ({
          ...prev,
          contactNumber: phone
        }));
        setContactPrefilled(true);
      }
    } catch (error) {
      console.warn('Unable to prefill contact number:', error);
    }
  }, [entityId, isAdminBooking, setBooking]);

  // Prefill contact for non-admin users
  useEffect(() => {
    if (!isAdminBooking && !contactPrefilled && entityId) {
      fetchCurrentUserContact();
    }
  }, [isAdminBooking, entityId, contactPrefilled, fetchCurrentUserContact]);

  // ===================================================================
  // EXTRACTED HOOKS - Admin User Selection
  // ===================================================================

  // Memoize contact prefill callback to prevent useEffect in useAdminUserSelection
  // from running on every render (which would re-prefill and prevent clearing the field)
  const handleContactPrefill = useCallback((phone) => {
    setBooking((prev) => ({ ...prev, contactNumber: phone }));
    setContactPrefilled(true);
  }, [setBooking]);

  // Admin user selection hook - manages user loading and selection for admin bookings
  const {
    users,
    usersLoading,
    selectedUserId,
    bypassAdvanceHours,
    handleUserChange,
    handleBypassChange,
  } = useAdminUserSelection({
    entityId,
    isAdminBooking,
    onContactPrefill: handleContactPrefill,
    t,
  });

  // ===================================================================
  // BOOKING PREFERENCES - Extract from entity settings
  // ===================================================================

  const canBookRecurring = entitySettings?.allow_recurring_bookings ?? currentEntity?.allow_recurring_bookings;
  const bookingRequiredConfirmation = entitySettings?.booking_required_confirmation ?? currentEntity?.booking_required_confirmation;
  const bookingWindowDays = entitySettings?.booking_window_days || currentEntity?.booking_window_days || 14;
  const minimumAdvanceHours = entitySettings?.minimum_advance_booking_hours || 0;
  const minimumAdvanceBookingScope = entitySettings?.minimum_advance_booking_scope || 'all_stops'; // Default to backward-compatible behavior
  const maximumLuggagePerRider = entitySettings?.maximum_luggage_per_rider || 0;
  const maximumLuggageEnforced = entitySettings?.maximum_luggage_per_rider_enforced || false;
  const maximumLuggageWarningMessage = entitySettings?.maximum_luggage_per_rider_warning_message;

  const rawVerificationEnabled = entitySettings?.booking_additional_verification_enabled;
  const additionalVerificationEnabled = rawVerificationEnabled === true ||
    rawVerificationEnabled === 'true' ||
    rawVerificationEnabled === 1 ||
    rawVerificationEnabled === '1';
  const additionalVerificationPrompt = entitySettings?.booking_additional_verification_prompt?.trim() || '';

  // Check if multi-language is enabled
  const multiLanguageEnabled = entitySettings?.multi_language_enabled;
  const accessibleBookingEnabled = asEnabledFlag(entitySettings?.accessible_service_enabled);
  const [accessibleBookingStatus, setAccessibleBookingStatus] = useState({
    loading: false,
    configured: null,
    failureReason: null,
    offlineMessage: ''
  });

  // ===================================================================
  // EXTRACTED HOOKS - Translated Preferences
  // ===================================================================

  // Translated preferences hook - fetches translated preference values
  const {
    translatedLuggageWarningMessage,
    translatedVerificationPrompt,
  } = useTranslatedPreferences({
    entityId,
    language: i18n.language,
    defaultLuggageWarning: maximumLuggageWarningMessage,
    defaultVerificationPrompt: additionalVerificationPrompt,
    entitySettings,
    multiLanguageEnabled,
  });

  const {
    routes,
    referenceDataMap,
    segmentDataMap, // Segment times for baseline ETA calculation
    loading,
    setLoading,
    fetchUserBookings,
  } = useBookingManagement({ currentEntity, entitySettings, i18n, t });

  const {
    appliedSchedules,
    availableDates,
    availableTimeSlots,
    selectedDates,
    setSelectedDates,
    bookAllAvailableDates,
    setBookAllAvailableDates,
    handleDateSelection,
    fetchAppliedSchedules,
    setAvailableTimeSlots,
    slotAvailability,
    slotAvailabilityLoading,
    slotAvailabilityDate,
    occurrencePreview,
    refreshSlotAvailability,
  } = useScheduleAvailability({
    currentEntity,
    booking,
    setBooking,
    canBookRecurring,
    bookingWindowDays,
    setLoading,
    t,
    setIsWaitlisted,
    addProcessingDetail,
  });

  const { selectedOccurrenceDate } = useMemo(() => deriveBookingOccurrenceScope({
    bookingDate: booking.date,
    selectedDates,
    availableDates,
    bookAllAvailableDates,
    canBookRecurring,
  }), [availableDates, bookAllAvailableDates, booking.date, canBookRecurring, selectedDates]);
  const isExactSelectionSnapshotCurrent = Boolean(
    selectedOccurrenceDate
    && !slotAvailabilityLoading
    && normalizeBookingOccurrenceDate(slotAvailabilityDate) === selectedOccurrenceDate
  );
  const selectedDelayIdentity = useMemo(() => resolveExactDelayIdentity({
    slotAvailability,
    slotAvailabilityDate,
    slotAvailabilityLoading,
    selectedOccurrenceDate,
    booking,
  }), [
    booking,
    selectedOccurrenceDate,
    slotAvailability,
    slotAvailabilityDate,
    slotAvailabilityLoading,
  ]);

  // ===================================================================
  // EXTRACTED HOOKS - Route Management
  // ===================================================================

  // Route management hook - handles route selection and location helpers
  const {
    getCurrentRoute,
    handleRouteChange,
  } = useRouteManagement({
    routes,
    booking,
    setBooking,
    setSelectedDates,
    setAvailableTimeSlots,
    setBookAllAvailableDates,
    setNormalizedBookingTime,
    fetchAppliedSchedules,
  });

  // Use entity timezone, falling back to browser timezone (more reliable than UTC)
  const entityTimezone = entitySettings?.time_zone || currentEntity?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const decoratedTimeSlots = useMemo(() => {
    if (!availableTimeSlots?.length) {
      return [];
    }

    const waypoints = getCurrentRoute()?.waypoints || [];
    const pickupStopId = booking.pickup ? parseInt(booking.pickup, 10) : null;
    const slotDateContext =
      slotAvailabilityDate ||
      (selectedDates.length > 0 ? selectedDates[0] : null) ||
      booking.date ||
      null;

    const filteredSlots = filterSlotsByAdvanceBooking({
      slots: availableTimeSlots,
      selectedDate: slotDateContext,
      minimumAdvanceHours,
      bookingScope: minimumAdvanceBookingScope,
      pickupStopId,
      waypoints,
      entityTimezone,
      bypassAdvanceBookingRestriction: bypassAdvanceHours, // Admin bypass flag
    });

    if (!slotAvailabilityLoading && booking.route && slotDateContext && !slotAvailabilityDate) {
      return filteredSlots.map((slot) => ({
        ...slot,
        disabled: true,
        disabledReason: 'availabilityUnresolved',
      }));
    }

    return enhanceSlotsWithCapacity({
      slots: filteredSlots,
      capacityData: slotAvailability,
      ridersRequested: booking.riders,
      luggageRequested: booking.luggage,
      requestAccessibleVehicle: booking.requestAccessibleVehicle,
      entityTimezone,
    });
  }, [
    availableTimeSlots,
    booking.date,
    booking.route,
    booking.luggage,
    booking.pickup,
    booking.requestAccessibleVehicle,
    booking.riders,
    bypassAdvanceHours, // Add to dependencies
    entityTimezone,
    getCurrentRoute,
    minimumAdvanceHours,
    minimumAdvanceBookingScope,
    selectedDates,
    slotAvailability,
    slotAvailabilityDate,
    slotAvailabilityLoading,
  ]);

  const fetchAccessibleBookingStatus = useCallback(async () => {
    if (!entityId) {
      return null;
    }

    setAccessibleBookingStatus((prev) => ({
      ...prev,
      loading: true
    }));

    try {
      const response = await axios.get(`/api/bookings/${entityId}/accessible-booking-status`);
      const statusData = response?.data?.data || {};
      setAccessibleBookingStatus({
        loading: false,
        configured: statusData.configured ?? null,
        failureReason: statusData.failureReason || null,
        offlineMessage: statusData.offlineMessage || ''
      });
      return statusData;
    } catch (error) {
      console.error('Failed to fetch accessible booking status:', error);
      setAccessibleBookingStatus({
        loading: false,
        configured: null,
        failureReason: 'accessible_online_booking_unavailable',
        offlineMessage: ''
      });
      toast.error(t('vehicleAssignment.accessibleOnlineBookingUnavailable'));
      return null;
    }
  }, [entityId, t]);

  const accessibleBookingBlocked = Boolean(
    booking.requestAccessibleVehicle && (
      accessibleBookingStatus.failureReason === 'no_accessible_vehicle_configured' ||
      accessibleBookingStatus.failureReason === 'accessible_online_booking_unavailable' ||
      accessibleBookingStatus.failureReason === 'accessible_service_disabled'
    )
  );
  const disableBookingFlowForAccessible = Boolean(
    booking.requestAccessibleVehicle && (
      accessibleBookingStatus.loading || accessibleBookingBlocked
    )
  );
  const accessibleBookingMessage = accessibleBookingStatus.failureReason === 'no_accessible_vehicle_configured'
    ? (accessibleBookingStatus.offlineMessage || t('vehicleAssignment.accessibleOfflineArrangementDefault'))
    : accessibleBookingStatus.failureReason === 'accessible_online_booking_unavailable'
      ? t('vehicleAssignment.accessibleOnlineBookingUnavailable')
    : accessibleBookingStatus.failureReason === 'accessible_service_disabled'
      ? t('vehicleAssignment.accessibleServiceDisabled')
      : '';

  useEffect(() => {
    const scheduleValue = typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time;
    const selectedTime = normalizeTimeValue(scheduleValue);
    if (!selectedTime || slotAvailabilityLoading) {
      return;
    }

    const normalizedRiders = Math.max(parseInt(booking.riders, 10) || 1, 1);
    const normalizedLuggage = Math.max(parseInt(booking.luggage, 10) || 0, 0);
    const matchingFixed = decoratedTimeSlots.find((slot) => slot.type === 'fixed' && slot.time === selectedTime);

    if (matchingFixed) {
      if (matchingFixed.disabled) {
        setBooking((prev) => ({
          ...prev,
          schedule: '',
          selectedTripId: null,
          selectedScheduleId: null
        }));
      }
      return;
    }

    const matchingOnCallSlots = decoratedTimeSlots.filter(
      (slot) =>
        slot.type === 'on_call' &&
        slot.startTime &&
        slot.endTime &&
        selectedTime >= slot.startTime &&
        selectedTime <= slot.endTime,
    );

    if (!matchingOnCallSlots.length) {
      setBooking((prev) => ({
        ...prev,
        schedule: '',
        selectedTripId: null,
        selectedScheduleId: null
      }));
      return;
    }

    const activeOnCallSlot = matchingOnCallSlots.find((slot) => !slot.disabled);
    if (!activeOnCallSlot) {
      setBooking((prev) => ({
        ...prev,
        schedule: '',
        selectedTripId: null,
        selectedScheduleId: null
      }));
      return;
    }

    const exactCapacityEntry = slotAvailability.find(
      (entry) => normalizeTimeValue(entry?.time) === selectedTime,
    );
    if (!exactCapacityEntry) {
      return;
    }

    if (exactCapacityEntry.bookingStopEligibility?.state
        && exactCapacityEntry.bookingStopEligibility.state !== 'available') {
      setBooking((prev) => ({
        ...prev,
        schedule: '',
        selectedTripId: null,
        selectedScheduleId: null
      }));
      return;
    }

    const pickupPassed = Boolean(
      exactCapacityEntry.pickupStopPassed ??
      exactCapacityEntry.pickup_stop_passed ??
      false,
    );
    if (pickupPassed) {
      setBooking((prev) => ({
        ...prev,
        schedule: '',
        selectedTripId: null,
        selectedScheduleId: null
      }));
      return;
    }

    const remainingSeats = Number(
      exactCapacityEntry.availableOnSegment ??
      exactCapacityEntry.available_on_segment ??
      exactCapacityEntry.availableAtPickup ??
      exactCapacityEntry.available_at_pickup ??
      exactCapacityEntry.remainingSeats ??
      exactCapacityEntry.remaining_seats ??
      activeOnCallSlot.remainingSeats ??
      0,
    );
    const totalCapacity = Number(
      exactCapacityEntry.vehicleCapacity ??
      exactCapacityEntry.vehicle_capacity ??
      activeOnCallSlot.totalCapacity ??
      0,
    );
    const remainingLuggage = Number(
      exactCapacityEntry.availableLuggageOnSegment ??
      exactCapacityEntry.available_luggage_on_segment ??
      exactCapacityEntry.availableLuggageAtPickup ??
      exactCapacityEntry.available_luggage_at_pickup ??
      exactCapacityEntry.remainingLuggage ??
      exactCapacityEntry.remaining_luggage ??
      activeOnCallSlot.remainingLuggage ??
      0,
    );
    const totalLuggageCapacity = Number(
      exactCapacityEntry.vehicleLuggageCapacity ??
      exactCapacityEntry.vehicle_luggage_capacity ??
      activeOnCallSlot.totalLuggageCapacity ??
      0,
    );
    const potentialWithReorder = Boolean(
      exactCapacityEntry.potentialWithReorder ??
      exactCapacityEntry.potential_with_reorder ??
      activeOnCallSlot.potentialWithReorder ??
      false,
    );

    const hasVehicle = totalCapacity > 0;
    const notEnoughSeats = hasVehicle && normalizedRiders > remainingSeats;
    const hasLuggageCapacity = totalLuggageCapacity > 0;
    const notEnoughLuggage = hasLuggageCapacity && normalizedLuggage > remainingLuggage;

    if ((notEnoughSeats || notEnoughLuggage) && !potentialWithReorder) {
      setBooking((prev) => ({ ...prev, schedule: '' }));
    }
  }, [
    booking.luggage,
    booking.riders,
    booking.schedule,
    decoratedTimeSlots,
    setBooking,
    slotAvailability,
    slotAvailabilityLoading,
  ]);

  // ===================================================================
  // EXTRACTED HOOKS - Booking Validation
  // ===================================================================

  // Validation hook - validates all booking fields and business rules
  const { validateBooking } = useBookingValidation({
    booking,
    routes,
    t,
    isAdminBooking,
    selectedUserId,
    canBookRecurring,
    bookAllAvailableDates,
    availableDates,
    selectedDates,
    maximumLuggagePerRider,
    maximumLuggageEnforced,
    additionalVerificationEnabled,
    setVerificationError,
  });

  // ===================================================================
  // EXTRACTED HOOKS - Booking Submission
  // ===================================================================

  // Submission hook - handles form submission and booking creation
  const { handleSubmit, submitBooking } = useBookingSubmission({
    booking,
    validateBooking,
    canBookRecurring,
    bookAllAvailableDates,
    selectedDates,
    availableDates,
    minimumAdvanceHours,
    minimumAdvanceBookingScope,
    isAdminBooking,
    bypassAdvanceHours,
    getCurrentRoute,
    maximumLuggagePerRider,
    bookingRequiredConfirmation,
    translatedVerificationPrompt,
    appliedSchedules,
    t,
    entityId,
    currentUserId,
    selectedUserId,
    entityTimezone,
    setNormalizedBookingTime,
    setShowPastDepartureDialog,
    setPastDepartureMessage,
    setIsConfirmModalOpen,
    setCurrentBookingDetails,
    setIsProcessing,
    setStatus,
    setIsWaitlisted,
    setBooking,
    setVerificationError,
    setContactPrefilled,
    setSelectedDates,
    setAvailableTimeSlots,
    setBookAllAvailableDates,
    fetchUserBookings,
    onBookingCreated,
    additionalVerificationEnabled,
    occurrencePreview,
    refreshSlotAvailability,
  });

  // Wrapper for handleSubmit that checks capacity first
  const handleSubmitWithCapacityCheck = useCallback(async (e) => {
    e.preventDefault();

    // Find the selected time slot to check capacity
    const bookingTime = normalizeTimeValue(
      typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time,
    );
    const selectedSlot = decoratedTimeSlots?.find((slot) => slot.type === 'fixed' && slot.time === bookingTime) ??
      decoratedTimeSlots?.find(
        (slot) => slot.type === 'on_call' && slot.startTime <= bookingTime && bookingTime <= slot.endTime,
      );

    const exactCapacityEntry = slotAvailability.find(
      (entry) => normalizeTimeValue(entry?.time) === bookingTime,
    );

    // Check if booking would exceed capacity
    // Only check when: vehicle is assigned (totalCapacity > 0) AND remainingSeats is known
    const requestedRiders = Math.max(parseInt(booking.riders, 10) || 1, 1);
    const requestedLuggage = Math.max(parseInt(booking.luggage, 10) || 0, 0);
    const remainingSeats = Number(
      exactCapacityEntry?.availableOnSegment ??
      exactCapacityEntry?.available_on_segment ??
      exactCapacityEntry?.availableAtPickup ??
      exactCapacityEntry?.available_at_pickup ??
      exactCapacityEntry?.remainingSeats ??
      exactCapacityEntry?.remaining_seats ??
      selectedSlot?.remainingSeats ??
      0,
    );
    const totalCapacity = Number(
      exactCapacityEntry?.vehicleCapacity ??
      exactCapacityEntry?.vehicle_capacity ??
      selectedSlot?.totalCapacity ??
      0,
    );
    const remainingLuggage = Number(
      exactCapacityEntry?.availableLuggageOnSegment ??
      exactCapacityEntry?.available_luggage_on_segment ??
      exactCapacityEntry?.availableLuggageAtPickup ??
      exactCapacityEntry?.available_luggage_at_pickup ??
      exactCapacityEntry?.remainingLuggage ??
      exactCapacityEntry?.remaining_luggage ??
      selectedSlot?.remainingLuggage ??
      0,
    );
    const totalLuggageCapacity = Number(
      exactCapacityEntry?.vehicleLuggageCapacity ??
      exactCapacityEntry?.vehicle_luggage_capacity ??
      selectedSlot?.totalLuggageCapacity ??
      0,
    );
    const potentialWithReorder = Boolean(
      exactCapacityEntry?.potentialWithReorder ??
      exactCapacityEntry?.potential_with_reorder ??
      selectedSlot?.potentialWithReorder ??
      false,
    );
    const hasVehicle = totalCapacity > 0;
    const wouldExceedPassengerCapacity = hasVehicle &&
      requestedRiders > remainingSeats;
    const wouldExceedLuggageCapacity = hasVehicle &&
      totalLuggageCapacity > 0 &&
      requestedLuggage > remainingLuggage;

    if (wouldExceedPassengerCapacity || wouldExceedLuggageCapacity) {
      // Only allow waitlist when the slot indicates a reorder could resolve capacity
      if (!potentialWithReorder) {
        toast.error(t('slotCapacityExceededForParty'));
        return;
      }

      // Store the event to replay after user confirms waitlist
      setPendingWaitlistSubmit({ originalEvent: e, selectedSlot });
      setShowWaitlistWarning(true);
      return;
    }

    // Capacity is fine, proceed with normal submission
    handleSubmit(e);
  }, [booking, decoratedTimeSlots, handleSubmit, slotAvailability, t]);

  // Handle waitlist confirmation - user accepts waitlist booking
  const handleWaitlistConfirm = useCallback(() => {
    setShowWaitlistWarning(false);
    // Proceed with the original submission
    if (pendingWaitlistSubmit?.originalEvent) {
      handleSubmit(pendingWaitlistSubmit.originalEvent);
    }
    setPendingWaitlistSubmit(null);
  }, [handleSubmit, pendingWaitlistSubmit]);

  // Handle waitlist cancel - user declines waitlist booking
  const handleWaitlistCancel = useCallback(() => {
    setShowWaitlistWarning(false);
    setPendingWaitlistSubmit(null);
  }, []);

  // ===================================================================
  // LOCAL STATE - Modals and UI
  // ===================================================================

  const [selectedBooking] = useState(null);

  // Boarding info modal state
  const [boardingInfoModal, setBoardingInfoModal] = useState({
    isOpen: false,
    waypointId: null,
    waypointName: '',
    initialBoardingInfo: null
  });

  const selectedTripId = useMemo(() => {
    const bookingTime = normalizeTimeValue(
      typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time,
    );
    if (!bookingTime) {
      return null;
    }

    const storedTripId = Number(booking.selectedTripId);
    if (!Number.isInteger(storedTripId) || storedTripId <= 0) {
      return null;
    }

    const exactCapacityEntry = slotAvailability.find(
      (entry) => (
        normalizeTimeValue(entry?.time) === bookingTime
        && Number(entry?.tripId ?? entry?.trip_id) === storedTripId
      ),
    );
    const tripId = exactCapacityEntry?.tripId ?? exactCapacityEntry?.trip_id ?? null;
    return Number.isInteger(Number(tripId)) ? Number(tripId) : null;
  }, [booking.schedule, booking.selectedTripId, slotAvailability]);

  const selectedOperationalDelay = selectedDelayIdentity.exact
    && selectedDelayIdentity.tripId === selectedTripId
    && selectedDelayIdentity.minutes > 0
    ? { originDepartureMinutes: selectedDelayIdentity.minutes }
    : null;

  // Helper function to check if waypoint has boarding info
  const hasBoardingInfo = (waypoint) => {
    return !!(
      waypoint.boarding_location_info ||
      waypoint.boarding_location_url ||
      waypoint.boarding_location_image
    );
  };

  // Helper function to open boarding info modal
  const handleOpenBoardingInfo = (waypoint) => {
    setBoardingInfoModal({
      isOpen: true,
      waypointId: waypoint.id,
      waypointName: waypoint.name,
      initialBoardingInfo: {
        text: waypoint.boarding_location_info || null,
        url: waypoint.boarding_location_url || null,
        imageUrl: waypoint.boarding_location_image
          ? `/uploads/stops/${waypoint.boarding_location_image}`
          : null,
        hasInfo: hasBoardingInfo(waypoint)
      }
    });
  };

  // Helper function to close boarding info modal
  const handleCloseBoardingInfo = () => {
    setBoardingInfoModal({
      isOpen: false,
      waypointId: null,
      waypointName: '',
      initialBoardingInfo: null
    });
  };

  // Clear verification value when verification is disabled
  useEffect(() => {
    if (!additionalVerificationEnabled && booking.entityVerificationValue) {
      setBooking(prev => ({
        ...prev,
        entityVerificationValue: ''
      }));
      setVerificationError('');
    }
  }, [additionalVerificationEnabled, booking.entityVerificationValue, setBooking, setVerificationError]);

  useEffect(() => {
    if (!accessibleBookingEnabled && booking.requestAccessibleVehicle) {
      setBooking((prev) => ({
        ...prev,
        requestAccessibleVehicle: false
      }));
      setAccessibleBookingStatus({
        loading: false,
        configured: null,
        failureReason: null,
        offlineMessage: ''
      });
    }
  }, [accessibleBookingEnabled, booking.requestAccessibleVehicle, setBooking]);

  // Check admin status from localStorage
  // Admin check now handled by isAdmin() from roleUtils

  // ===================================================================
  // LOCAL EFFECTS
  // ===================================================================

  // Booking status constants
  const bookingStatuses = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    CANCELED: 'canceled',
    FINISHED: 'finished',
    NO_SHOW: 'no_show',
    WAITLISTED: 'waitlisted'
  };

  // ===================================================================
  // EVENT HANDLERS
  // ===================================================================

  /**
   * Handle input change for form fields
   * Includes special logic for pickup/dropoff validation and verification error clearing
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const normalizedValue = type === 'checkbox' ? checked : value;
    const currentRoute = getCurrentRoute();
    const getWaypointById = (waypointId) => currentRoute?.waypoints?.find(
      (waypoint) => String(waypoint.id) === String(waypointId),
    );

    if (name === 'requestAccessibleVehicle') {
      setBooking(prev => ({
        ...prev,
        requestAccessibleVehicle: checked
      }));

      if (!checked) {
        setAccessibleBookingStatus({
          loading: false,
          configured: null,
          failureReason: null,
          offlineMessage: ''
        });
      } else {
        fetchAccessibleBookingStatus();
      }
      return;
    }

    // Clear verification error when user types
    if (name === 'entityVerificationValue') {
      setVerificationError('');
    }

    const currentPickupWaypoint = getWaypointById(booking.pickup);
    const currentDropoffWaypoint = getWaypointById(booking.dropoff);
    const nextPickupWaypoint = name === 'pickup' ? getWaypointById(normalizedValue) : currentPickupWaypoint;
    const nextDropoffWaypoint = name === 'dropoff' ? getWaypointById(normalizedValue) : currentDropoffWaypoint;
    const sameAddressMessage = t('pickupAndDropoffAddressesAreSame');

    if (name === 'dropoff' && haveSameLocationAddress(currentPickupWaypoint, nextDropoffWaypoint)) {
      toast.error(sameAddressMessage);
      return;
    }

    // If changing pickup location and it matches the current dropoff address, clear dropoff
    if (name === 'pickup' && haveSameLocationAddress(nextPickupWaypoint, currentDropoffWaypoint)) {
      setBooking(prev => ({
        ...prev,
        [name]: normalizedValue,
        dropoff: ''
      }));
      toast.error(sameAddressMessage);
    } else {
      setBooking(prev => ({
        ...prev,
        [name]: normalizedValue
      }));
    }
  };

  // Handle confirmation of past departure booking
  const handlePastDepartureConfirm = async () => {
    setShowPastDepartureDialog(false);

    // Continue with normal booking flow - prepare booking details
    const dates = bookAllAvailableDates ? availableDates :
                  canBookRecurring ? selectedDates :
                  [formatDateISO(booking.date)];

    const scheduleValue = typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time;
    const normalizedTime = normalizeBookingTime(scheduleValue);

    setNormalizedBookingTime(normalizedTime);

    const bookingDetails = {
      routeName: getCurrentRoute()?.name,
      bookingMode: bookAllAvailableDates ? 'all' : canBookRecurring ? 'multiple' : 'single',
      dates: dates.map(date => formatDateLocal(date)),
      time: booking.schedule,
      timeValue: normalizedTime,
      passengers: maximumLuggagePerRider > 0
        ? `${booking.riders} ${t('riders')}, ${booking.luggage} ${t('luggage')}`
        : `${booking.riders} ${t('riders')}`,
      pickup: (() => {
        const pickupStop = getCurrentRoute()?.waypoints?.find(s => s.id === parseInt(booking.pickup));
        return pickupStop ? `${pickupStop.name}: ${shortenAddress(pickupStop.address)}` : '';
      })(),
      dropoff: (() => {
        const dropoffStop = getCurrentRoute()?.waypoints?.find(s => s.id === parseInt(booking.dropoff));
        return dropoffStop ? `${dropoffStop.name}: ${shortenAddress(dropoffStop.address)}` : '';
      })(),
      contactNumber: booking.contactNumber,
      specialRequests: booking.specialRequests,
      entityVerificationValue: booking.entityVerificationValue?.trim() || '',
      verificationPrompt: translatedVerificationPrompt,
      startDate: parseLocalDate(availableDates[0]),
      endDate: parseLocalDate(appliedSchedules[0].end_date),
      bookingStatus: bookingRequiredConfirmation ? 'pending' : 'confirmed',
      confirmPastDeparture: true // Flag to indicate user confirmed past departure
    };

    setCurrentBookingDetails(bookingDetails);
    setIsConfirmModalOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      [bookingStatuses.CONFIRMED]: 'bg-green-600',
      [bookingStatuses.CANCELED]: 'bg-red-600',
      [bookingStatuses.PENDING]: 'bg-yellow-600',
      [bookingStatuses.FINISHED]: 'bg-blue-600',
      [bookingStatuses.NO_SHOW]: 'bg-gray-600',
      [bookingStatuses.WAITLISTED]: 'bg-purple-600'
    };
    return colors[status] || 'bg-gray-600';
  };

  if (loading) {
    return (
      <div className={embedded ? "w-full py-8 text-center" : "max-w-md mx-auto p-4 text-center"}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!entityId) {
    return null;
  }

  const containerClasses = embedded
    ? cn("w-full mt-0", background.primary)
    : cn("max-w-md mx-auto mt-8", background.primary);

  const cardClasses = embedded
    ? cn(background.secondary, "p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700")
    : cn(background.secondary, "p-6");

  return (
    <div className={containerClasses}>
      <ThemedCard className={cardClasses}>
        <div className={cn("pb-4 border-b mb-6", embedded ? "border-gray-700" : "border-gray-200")}>
          <h2 className={cn("text-xl font-semibold", text.primary)}>
            {selectedBooking ? t('editBooking') : isAdminBooking ? t('createBooking') : t('bookAShuttle')}
          </h2>
          <p className={cn("mt-1", text.secondary)}>
            {isWaitlisted
              ? t('youWillBeAddedToTheWaitlist')
              : isAdminBooking
                ? t('enterBookingDetailsBelow')
                : t('enterYourBookingDetailsBelow')}
          </p>
        </div>

        {/* Admin User Selection Section */}
        {isAdminBooking && (
          <AdminUserSelectionSection
            users={users}
            usersLoading={usersLoading}
            selectedUserId={selectedUserId}
            bypassAdvanceHours={bypassAdvanceHours}
            onUserChange={handleUserChange}
            onBypassChange={handleBypassChange}
          />
        )}

        <form onSubmit={handleSubmitWithCapacityCheck} className="space-y-4">
          {accessibleBookingEnabled && (
            <div className={cn(
              'rounded-xl border p-4',
              accessibleBookingBlocked
                ? 'border-amber-300 bg-amber-50'
                : 'border-blue-200 bg-blue-50'
            )}>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="requestAccessibleVehicle"
                  checked={Boolean(booking.requestAccessibleVehicle)}
                  onChange={handleInputChange}
                  className="mt-1"
                />
                <div>
                  <div className={cn('text-sm font-medium', text.primary)}>
                    {t('requestAccessibleVehicle')}
                  </div>
                  <div className={cn('text-xs mt-1', text.secondary)}>
                    {t('requestAccessibleVehicleHelp')}
                  </div>
                </div>
              </label>

              {booking.requestAccessibleVehicle && accessibleBookingStatus.loading && (
                <div className={cn('mt-3 text-sm', text.secondary)}>
                  {t('checkingAccessibleBookingStatus')}
                </div>
              )}

              {booking.requestAccessibleVehicle && accessibleBookingMessage && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-white/80 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{accessibleBookingMessage}</span>
                </div>
              )}
            </div>
          )}

          <fieldset
            disabled={disableBookingFlowForAccessible}
            className={cn(
              'min-w-0',
              disableBookingFlowForAccessible ? 'space-y-4 opacity-60' : 'space-y-4'
            )}
          >
            {/* Route Selection Section */}
            <RouteSelectionSection
              routes={routes}
              selectedRoute={booking.route}
              onChange={handleRouteChange}
              loading={loading}
            />

            {/* Location Selection Section (pickup/dropoff + route overview) */}
            <LocationSelectionSection
              currentRoute={getCurrentRoute()}
              pickupValue={booking.pickup}
              dropoffValue={booking.dropoff}
              onChange={handleInputChange}
              onOpenBoardingInfo={handleOpenBoardingInfo}
              hasBoardingInfo={hasBoardingInfo}
            />

            {/* Passenger Details Section (riders + luggage) - above date/time since these impact slot availability */}
            <PassengerDetailsSection
              riders={booking.riders}
              luggage={booking.luggage}
              onChange={handleInputChange}
              maximumLuggagePerRider={maximumLuggagePerRider}
              maximumLuggageEnforced={maximumLuggageEnforced}
              luggageWarningMessage={translatedLuggageWarningMessage}
            />

            {/* Date Selection Section */}
            {booking.route && (
              <DateSelectionSection
                canBookRecurring={canBookRecurring}
                bookAllAvailableDates={bookAllAvailableDates}
                selectedDates={selectedDates}
                selectedDate={booking.date}
                availableDates={availableDates}
                bookingWindowDays={bookingWindowDays}
                onBookAllChange={setBookAllAvailableDates}
                onDateSelect={handleDateSelection}
              />
            )}

            {/* Time Selection Section */}
            {(bookAllAvailableDates || booking.route) && (
              <TimeSelectionSection
                slots={decoratedTimeSlots}
                booking={booking}
                selectedTime={booking.schedule}
                onSelect={(time, metadata = {}) => setBooking(prev => ({
                  ...prev,
                  schedule: time,
                  selectedTripId: metadata.slot?.tripId ?? metadata.slot?.trip_id ?? null,
                  selectedScheduleId:
                    metadata.slot?.scheduleId
                    ?? metadata.slot?.occurrenceScheduleId
                    ?? null
                }))}
                selectedDates={selectedDates}
                availableDates={availableDates}
                bookAllAvailableDates={bookAllAvailableDates}
                canBookRecurring={canBookRecurring}
                schedule={appliedSchedules.find(s =>
                  s.active &&
                  isDateInRange(new Date(selectedDates[0] || booking.date), s.start_date, s.end_date) &&
                  isDayIncluded(new Date(selectedDates[0] || booking.date), s.included_days) &&
                  !isDateExcluded(new Date(selectedDates[0] || booking.date), s.excluded_periods)
                )}
                RouteId={parseInt(booking.route)}
                waypoints={getCurrentRoute()?.waypoints}
                pickupStopId={parseInt(booking.pickup)}
                dropoffStopId={parseInt(booking.dropoff)}
                referenceData={referenceDataMap[parseInt(booking.route)]}
                segmentData={segmentDataMap[parseInt(booking.route)]}
                entityId={entityId}
                currentEntity={currentEntity}
                userRole={currentEntity.userRole}
                findApplicableSchedule={findApplicableSchedule}
                appliedSchedules={appliedSchedules}
                theme={{ cn, background, text }}
                minimumAdvanceHours={minimumAdvanceHours}
                minimumAdvanceBookingScope={minimumAdvanceBookingScope}
                slotAvailability={slotAvailability}
                slotAvailabilityLoading={slotAvailabilityLoading}
                slotAvailabilityDate={slotAvailabilityDate}
                selectedOccurrenceDate={selectedOccurrenceDate}
                isExactSelectionSnapshotCurrent={isExactSelectionSnapshotCurrent}
              />
            )}

            {/* Contact & Verification Section (contact, verification, special requests) */}
            <ContactVerificationSection
              contactNumber={booking.contactNumber}
              specialRequests={booking.specialRequests}
              entityVerificationValue={booking.entityVerificationValue}
              onChange={handleInputChange}
              additionalVerificationEnabled={additionalVerificationEnabled}
              verificationPrompt={translatedVerificationPrompt}
              verificationError={verificationError}
            />


            {status && (
              <div className={`p-4 rounded-md text-white ${getStatusColor(status)}`}>
                <h4 className="font-semibold">
                  {t('booking')} {t(status)}
                </h4>
                <p>
                  {status === bookingStatuses.CONFIRMED && t('Your booking has been confirmed!')}
                  {status === bookingStatuses.CANCELED && t('Your booking has been canceled.')}
                  {status === bookingStatuses.PENDING && t('Your booking is pending confirmation...')}
                  {status === bookingStatuses.WAITLISTED && t('You have been added to the waitlist.')}
                  {status === bookingStatuses.NO_SHOW && t('Marked as no-show.')}
                  {status === bookingStatuses.FINISHED && t('Ride completed.')}
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <ThemedButton
                type="submit"
                disabled={submitting || [bookingStatuses.CONFIRMED, bookingStatuses.CANCELED].includes(status)}
                variant="primary"
                className="flex-1"
              >
                {submitting ? t('Creating Booking...') :
                isWaitlisted ? t('Join Waitlist') : t('bookNow')}
              </ThemedButton>
            </div>
          </fieldset>
        </form>

        {/* Booking Confirmation Dialog */}
        <BookingConfirmDialog
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={submitBooking}
          bookingDetails={currentBookingDetails}
          isProcessing={isProcessing}
          waypoints={getCurrentRoute()?.waypoints}
          pickupStopId={parseInt(booking.pickup)}
          dropoffStopId={parseInt(booking.dropoff)}
          referenceData={referenceDataMap[parseInt(booking.route)]}
          segmentData={segmentDataMap[parseInt(booking.route)]}
          entityId={entityId}
          tripId={selectedTripId}
          operationalDelay={selectedOperationalDelay}
        />

        {/* Past Departure Time Confirmation Dialog */}
        <ConfirmDialogShell
          isOpen={showPastDepartureDialog}
          onClose={() => setShowPastDepartureDialog(false)}
          onConfirm={handlePastDepartureConfirm}
          title={t('confirmBooking')}
          confirmLabel={t('Confirm')}
          cancelLabel={t('Cancel')}
          confirmVariant="warning"
          showIcon={true}
          iconVariant="warning"
          isLoading={isProcessing}
        >
          <p className={cn("text-center", text.primary)}>
            {pastDepartureMessage}
          </p>
        </ConfirmDialogShell>
      </ThemedCard>

      {/* Boarding Info Modal */}
      <BoardingInfoModal
        isOpen={boardingInfoModal.isOpen}
        onClose={handleCloseBoardingInfo}
        waypointId={boardingInfoModal.waypointId}
        waypointName={boardingInfoModal.waypointName}
        entityId={entityId}
        initialBoardingInfo={boardingInfoModal.initialBoardingInfo}
      />

      {/* Waitlist Warning Dialog - shown when booking would exceed capacity */}
      {showWaitlistWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={cn(
            'max-w-md w-full mx-4 p-6 rounded-lg shadow-xl',
            'bg-white dark:bg-gray-800'
          )}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className={cn('text-lg font-semibold mb-2', text.primary)}>
                  {t('waitlistBookingTitle')}
                </h3>
                <p className={cn('text-sm mb-4', text.secondary)}>
                  {t('waitlistBookingWarning')}
                </p>
                <ul className={cn('text-sm mb-4 list-disc list-inside space-y-1', text.secondary)}>
                  <li>{t('waitlistBookingPoint1')}</li>
                  <li>{t('waitlistBookingPoint2')}</li>
                  <li>{t('waitlistBookingPoint3')}</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleWaitlistCancel}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600',
                  text.primary
                )}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleWaitlistConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
              >
                {t('waitlistBookingConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCreateForm;
